import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useApi } from '~/composables/useApi'
import { formatPrice } from '~/utils/currency'
import { mapUserProfile } from '~/utils/user'

export const useUserStore = defineStore('user', () => {
  const profile = ref(null)
  const pending = ref(true)
  const error = ref(null)
  const initialized = ref(false)
  const logoutPending = ref(false)

  let inflight = null

  const isAuthenticated = computed(() => Boolean(profile.value))

  const formattedBalance = computed(() =>
    profile.value ? formatPrice(profile.value.balance) : formatPrice(0),
  )

  async function fetchProfile() {
    if (inflight) {
      return inflight
    }

    const api = useApi()
    pending.value = true
    error.value = null

    inflight = (async () => {
      try {
        const response = await api('/api/v1/user')
        profile.value = mapUserProfile(response.data)
      } catch (err) {
        error.value = err
        profile.value = null
      } finally {
        pending.value = false
        initialized.value = true
        inflight = null
      }

      return profile.value
    })()

    return inflight
  }

  async function ensureProfile() {
    if (!initialized.value) {
      await fetchProfile()
    }
    return profile.value
  }

  async function logout() {
    const api = useApi()
    logoutPending.value = true

    try {
      await api('/logout', { method: 'POST' })
      profile.value = null
      window.location.assign(import.meta.env.BASE_URL)
    } finally {
      logoutPending.value = false
    }
  }

  return {
    profile,
    pending,
    error,
    initialized,
    logoutPending,
    isAuthenticated,
    formattedBalance,
    fetchProfile,
    ensureProfile,
    logout,
  }
})
